import * as React from "react";
import { useFormApi, useFormState } from "informedv4";
import { Dropdown } from "../../../../forms/informedv4/DropdownV4";
import { Required } from "../../../../forms/validation/Required";
import { Subject, Visit, Session } from "webportalclient";
import { GetSessionLabel } from "../../../Request/Selectors/SiteOptions";
import { useCurrentStudyConfigurationContext } from "../../../../Core/CurrentStudyConfigurationContext";
import Loading, { FontSize } from "../../../../misc/components/loading";
import { useWebPortalApiClient } from "../../../../misc/api/WebPortalTokenClient";

export interface SelectedEntities {
    Subject: Subject;
    Visit: Visit;
    Session: Session;
}

interface SwapLabelsSelectorProps {
    subjectIdFieldDisplayText: string;
    originalSubjectValue: Subject;
    originalVisitValue: Visit;
    originalSessionValue: Session;
    siteId: string;
    onNext: (form: SelectedEntities) => void;
}

export interface SwapLabelsSelectorForm {
    Subject: string;
    Visit: string;
    Session: string;
}

export const SwapLabelsSelector = ({
    subjectIdFieldDisplayText,
    originalSubjectValue,
    originalVisitValue,
    originalSessionValue,
    siteId,
    onNext
}: SwapLabelsSelectorProps): JSX.Element => {
    const formApi = useFormApi();
    const formState = useFormState();
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    const [loadingSubjects, setLoadingSubjects] = React.useState<boolean>(false);
    const [subjects, setSubjects] = React.useState<Subject[]>([]);
    const [loadingVisits, setLoadingVisits] = React.useState<boolean>(false);
    const [visits, setVisits] = React.useState<Visit[]>([]);
    const [loadingSessions, setLoadingSessions] = React.useState<boolean>(false);
    const [sessions, setSessions] = React.useState<Session[]>([]);

    const fetchSubjects = async () => {
        setLoadingSubjects(true);
        const subjects = await client.client.getSubjects(`id,createdWithSpirotracV,status,number`, `siteId eq ${siteId}`, "number", "site");

        setSubjects(subjects.value);
        setLoadingSubjects(false);
    };

    const fetchVisits = async (stateValues: SwapLabelsSelectorForm) => {
        if (!stateValues.Subject) {
            setVisits([]);
            return;
        }
        setLoadingVisits(true);
        const visits = await client.client.getVisits(`created,modified,visitScheduleItemId,id,subjectId,name,occurred`, `subjectId eq ${stateValues.Subject}`, "name");

        setVisits(visits.value);
        setLoadingVisits(false);
    };

    const fetchSessions = async (stateValues: SwapLabelsSelectorForm) => {
        if (!stateValues.Visit) {
            setSessions([]);
            return;
        }
        setLoadingSessions(true);
        const originalSessionResult = await client.client.getSession(originalSessionValue.id, "id,sessionType");
        const sessions = await client.client.getSessions("id,name,occurred,suppressed", `visitId eq ${stateValues.Visit} and sessionType eq '${originalSessionResult.sessionType}'`, "name");

        setSessions(sessions.value);
        setLoadingSessions(false);
    }

    React.useEffect(() => {
        const fetchData = async () => {
            await fetchSubjects();
        }

        fetchData();
    }, [study.id]);

    React.useEffect(() => {
        const fetchData = async () => {
            const state = formApi.getFormState();
            await fetchVisits(state.values as unknown as SwapLabelsSelectorForm);
        }

        fetchData();
    }, [subjects, formApi, formState.values.Subject]);

    React.useEffect(() => {
        const fetchData = async () => {
            const state = formApi.getFormState();
            await fetchSessions(state.values as unknown as SwapLabelsSelectorForm);
        }

        fetchData();
    }, [visits, formApi, formState.values.Visit]);

    const onSubmit = () => {
        formApi.validate();

        const state = formApi.getFormState()
        if (state.invalid)
            return;

        onNext({
            Subject: subjects.find(s => s.id === state.values.Subject),
            Visit: visits.find(v => v.id === state.values.Visit),
            Session: sessions.find(s => s.id === state.values.Session)
        });
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <p className="text-muted" />
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>Current {subjectIdFieldDisplayText}</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                readOnly
                                value={originalSubjectValue.number}
                            />
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>Current Visit Label</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                readOnly
                                value={originalVisitValue.name}
                            />
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>Current Session Label</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                readOnly
                                value={GetSessionLabel(originalSessionValue)}
                            />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>{subjectIdFieldDisplayText}</label>&nbsp;
                            {loadingSubjects ? <Loading
                                className="ml-1"
                                style={{ display: "inline-block" }}
                                size={FontSize.OneX}
                            /> : null}
                            <Dropdown
                                name="Subject"
                                id="Subject"
                                field="Subject"
                                className="form-control"
                                disabled={loadingSubjects}
                                onChange={_ => {
                                    formApi.setValue("Visit", null);
                                    formApi.setValue("Session", null);
                                }}
                                validate={Required}
                            >
                                <option key={`SubjectOptionBlank`} value={null} />
                                {subjects.map(s => (
                                    <option key={`SubjectOption${s.id}`} value={s.id}>
                                        {s.number}
                                    </option>
                                ))}
                            </Dropdown>
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>New Visit Label</label>&nbsp;
                            {loadingVisits || loadingSubjects ? <Loading
                                className="ml-1"
                                style={{ display: "inline-block" }}
                                size={FontSize.OneX}
                            /> : null}
                            <Dropdown
                                name="Visit"
                                id="Visit"
                                field="Visit"
                                className="form-control"
                                disabled={loadingVisits || loadingSubjects}
                                onChange={_ => {
                                    formApi.setValue("Session", null);
                                }}
                                validate={Required}
                            >
                                <option key={`VisitOptionBlank`} value={null} />
                                {visits.map(v => (
                                    <option key={`VisitOption${v.id}`} value={v.id}>
                                        {v.name}
                                    </option>
                                ))}
                            </Dropdown>
                        </div>
                        <div className="form-group col-sm-4 col-xs-12">
                            <label>New Session Label</label>&nbsp;
                            {loadingVisits || loadingSubjects || loadingSessions ? <Loading
                                className="ml-1"
                                style={{ display: "inline-block" }}
                                size={FontSize.OneX}
                            /> : null}
                            <Dropdown
                                name="Session"
                                id="Session"
                                field="Session"
                                disabled={loadingSessions || loadingVisits || loadingSubjects}
                                className="form-control"
                                validate={Required}
                            >
                                <option key={`SessionOptionBlank`} value={null} />
                                {sessions.map(s => (
                                    <option key={`SessionOption${s.id}`} value={s.id}>
                                        {GetSessionLabel(s)}
                                    </option>
                                ))}
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <button className="btn btn-primary" type="submit" onClick={() => onSubmit()}>
                    Next
                </button>
            </div>
        </>
    );
};
